import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { TeamPresenceProvider } from "./contexts/TeamPresenceContext";
import { ChatPresenceProvider } from "./contexts/ChatPresenceContext";
import { RightSidebarProvider } from "./contexts/RightSidebarContext";
import { LayoutProvider, useLayout } from "./contexts/LayoutContext";
import { PopupProvider, usePopup } from "./contexts/PopupContext";
import { PanelsProvider } from "./contexts/PanelsContext";
import { RightClickMenuProvider } from "./contexts/RightClickMenuContext";
import { RefsProvider, useRefs } from "./contexts/RefsContext";

import MainLeftSidebar from "./Navigation/Sidebar/MainLeftSidebar";
import MainNavbar from "./Navigation/Navbar/MainNavbar";
import MainHome from "./Center/Home/MainHome";
import MainChat from "./Center/Chat/MainChat";
import MainExplorer from "./Center/Explorer/MainExplorer";
import MainGraph from "./Center/Graph/MainGraph";
import MainFeeds from "./Center/Feeds/MainFeeds";
import MainWiki from "./Center/Wiki/MainWiki";
import MainReports from "./Center/Reports/MainReports";
import { MainApts } from "./Center/Apts/MainApts";
import LoadingSpinner from "./Universal/LoadingSpinner";
import SidebarPortals from "./Navigation/Sidebar/SidebarPortals";
import ScreenshotPopUp from "./Universal/ScreenshotPopUp";
import PopUpNotesWindow from "./Universal/PopUpNotesWindow";
import PopUpTicketsWindow from "./Universal/PopUpTicketsWindow";
import RightClickMenu from "./contexts/RightClickMenu";
import TeamToastContainer from "./contexts/TeamToastContainer";
import { SearchProvider } from "./Navigation/Search/useSearch";
import MainSearch from "./Navigation/Search/MainSearch";
import { SearchRegistryProvider } from "./Navigation/Search/SearchRegistry";
import FloatingChatButton from "./Universal/FloatingChatButton";
import { CompanyProvider } from "../context";
import { useCompanyContext } from "../context";

/* ─── Build variant flags ─── */
const isII = process.env.REACT_APP_IS_II === "true";
const isCP = process.env.REACT_APP_IS_CP === "true";
// CP shows everything; II hides wiki + apts
const showWiki = isCP || !isII;
const showApts = isCP || !isII;

/* ─── Page transition variants ─── */
const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

const pageTransition = {
  duration: 0.2,
  ease: [0.25, 0.46, 0.45, 0.94],
};

function MainInner() {
  const { captureRef } = useRefs();
  const { companyKey, companies } = useCompanyContext();
  const location = useLocation();

  // Only animate on top-level route changes (home, chat, explorer, …)
  // so sub-route navigation (e.g. /chat/thread1 → /chat/thread2) stays instant
  const routeKey = location.pathname.split("/")[1] || "home";

  const hasCompany = companies.length > 0;

  const AGENT_ROUTES = {
    ...(showWiki ? { "/wiki": "WIKI" } : {}),
    ...(showApts ? { "/apts": "APTS" } : {}),
  };

  const {
    leftOpen,
    LEFT_WIDTH,
    hoverLeft,
    setHoverLeft,
    forcedLeft,
    setForcedLeft,
    rightOpen,
    rightWidth,
    setRightWidth,
    rightComponent,
    hoverRight,
    setHoverRight,
    forcedRight,
    setForcedRight,
    isResizingRight,
    resizeStartX,
    resizeStartWidth,
    headerMarginLeft,
    centerMarginLeft,
    centerMarginRight,
    currentAppPage,
  } = useLayout();

  const { isPopUpOpen, setIsPopUpOpen, popUpComponent, isScreenshotPopUpOpen } =
    usePopup();

  return (
    <div
      ref={captureRef}
      className="relative flex flex-col h-screen bg-mainBg text-mainText font-poppins font-normal"
    >
      <PopUpNotesWindow />
      <PopUpTicketsWindow />
      <MainSearch />
      <header
        className="absolute z-30 h-10 transition-[inset] duration-300 ease-in-out"
        style={{ left: headerMarginLeft, right: 0 }}
      >
        <MainNavbar />
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        <div className="flex flex-1 overflow-hidden relative">
          <aside
            className={`fixed inset-y-0 left-0 bg-mainBg transition-transform duration-300 ease-in-out z-[1] ${
              leftOpen
                ? "translate-x-0 shadow-xl shadow-mainShadow"
                : "-translate-x-full shadow-none"
            }`}
            style={{ width: LEFT_WIDTH }}
          >
            <MainLeftSidebar />
          </aside>

          <SidebarPortals />

          <main
            className="relative z-0 flex-1 flex flex-col overflow-auto transition-[margin] duration-300 ease-in-out h-full bg-mainBg"
            style={{
              marginLeft: centerMarginLeft,
              marginRight: centerMarginRight,
            }}
          >
            {!hasCompany && process.env.REACT_APP_IS_DD ? (
              <div className="flex flex-col items-center justify-center h-full gap-y-2 select-none pointer-events-none">
                <p className="text-[13px] text-mutedText/50 tracking-wide">
                  Add a company to get started
                </p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={routeKey}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={pageTransition}
                  className="flex flex-col flex-1 h-full"
                >
                  <Routes location={location}>
                    <Route
                      path="home"
                      element={<MainHome key={companyKey} />}
                    />
                    <Route
                      path="chat/*"
                      element={<MainChat key={companyKey} />}
                    />
                    <Route
                      path="explorer/*"
                      element={<MainExplorer key={companyKey} />}
                    />
                    <Route
                      path="graph/*"
                      element={<MainGraph key={companyKey} />}
                    />
                    <Route
                      path="feed/*"
                      element={<MainFeeds key={companyKey} />}
                    />
                    {showApts && (
                      <Route
                        path="apts/*"
                        element={<MainApts key={companyKey} />}
                      />
                    )}
                    <Route
                      path="reports/*"
                      element={<MainReports key={companyKey} />}
                    />
                    {showWiki && (
                      <Route
                        path="wiki/*"
                        element={<MainWiki key={companyKey} />}
                      />
                    )}
                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </motion.div>
              </AnimatePresence>
            )}
          </main>

          <aside
            className="fixed top-10 bottom-0 right-0 bg-mainBg shadow-xl shadow-mainShadow transition-[width] duration-300 ease-in-out z-[1] overflow-hidden"
            style={{ width: rightOpen ? rightWidth : 0 }}
            onMouseEnter={() => setHoverRight(true)}
            onMouseLeave={() => {
              setHoverRight(false);
              if (!forcedRight) return;
              setTimeout(() => {
                if (!hoverRight) setForcedRight(false);
              }, 1000);
            }}
          >
            {rightOpen && (
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  isResizingRight.current = true;
                  resizeStartX.current = e.clientX;
                  resizeStartWidth.current = rightWidth;
                  document.body.style.cursor = "col-resize";
                  document.body.style.userSelect = "none";

                  const onMove = (ev) => {
                    if (!isResizingRight.current) return;
                    const delta = resizeStartX.current - ev.clientX;
                    const next = Math.min(
                      900,
                      Math.max(360, resizeStartWidth.current + delta),
                    );
                    setRightWidth(next);
                  };
                  const onUp = () => {
                    isResizingRight.current = false;
                    document.body.style.cursor = "";
                    document.body.style.userSelect = "";
                    document.removeEventListener("mousemove", onMove);
                    document.removeEventListener("mouseup", onUp);
                    setRightWidth((w) => {
                      localStorage.setItem("rightSidebarWidth", String(w));
                      return w;
                    });
                  };
                  document.addEventListener("mousemove", onMove);
                  document.addEventListener("mouseup", onUp);
                }}
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
              >
                <div className="absolute left-0 top-0 bottom-0 w-px bg-transparent group-hover:bg-velBlue/50 transition-colors" />
              </div>
            )}

            <div className="h-full overflow-auto">{rightComponent}</div>
          </aside>
        </div>

        {AGENT_ROUTES[currentAppPage] && (
          <FloatingChatButton agent={AGENT_ROUTES[currentAppPage]} />
        )}

        {isPopUpOpen && (
          <div
            className="fixed inset-0 bg-mainBg/60 z-40 flex items-center justify-center p-6 backdrop-blur-sm"
            onClick={() => setIsPopUpOpen(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              {popUpComponent ? popUpComponent : <LoadingSpinner />}
            </div>
          </div>
        )}

        {isScreenshotPopUpOpen && (
          <div className="absolute flex w-full h-full inset-0 bg-mainBg z-40">
            <ScreenshotPopUp />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Main() {
  return (
    <CompanyProvider>
      <PopupProvider>
        <PanelsProvider>
          <SearchProvider>
            <SearchRegistryProvider>
              <RightSidebarProvider>
                <RefsProvider>
                  <TeamPresenceProvider>
                    <ChatPresenceProvider>
                      <RightClickMenuProvider>
                        <LayoutProvider>
                          <MainInner />
                          <RightClickMenu />
                          <TeamToastContainer />
                        </LayoutProvider>
                      </RightClickMenuProvider>
                    </ChatPresenceProvider>
                  </TeamPresenceProvider>
                </RefsProvider>
              </RightSidebarProvider>
            </SearchRegistryProvider>
          </SearchProvider>
        </PanelsProvider>
      </PopupProvider>
    </CompanyProvider>
  );
}
